import { ANALYTICS_EVENTS } from '../constants/analytics';
import { safeAnalytics } from './analytics';

/**
 * `Voice Recording Recovery Offered` usually fires on the first render after
 * sign-in, which is before `main.tsx`'s idle-deferred `safeAnalytics.init()`,
 * and `track()` drops anything sent before init with no queue. It is the
 * denominator of the offered-to-restored funnel, so a dropped one makes the
 * recovery rate read high. Same remedy as `authPersistenceRecovery.ts` (the
 * model `rules/analytics.md` names): stash, then flush after init and on every
 * consent grant, through `flushPendingAnalyticsEvents` in
 * `analyticsConsentGrant.ts`.
 */

const PENDING_KEY = 'bower:pending_recording_recovery_offered';

export type RecoveryOfferedProps = {
  workspace_id: string;
  duration_ms: number;
  was_complete: boolean;
  age_ms: number;
};

/** sessionStorage is client-writable and becomes event properties: input, not payload. */
function isOfferedProps(value: unknown): value is RecoveryOfferedProps {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.workspace_id === 'string' &&
    /^[0-9a-f-]{36}$/i.test(v.workspace_id) &&
    typeof v.duration_ms === 'number' &&
    typeof v.was_complete === 'boolean' &&
    typeof v.age_ms === 'number' &&
    Object.keys(v).length === 4
  );
}

function readStash(): RecoveryOfferedProps[] {
  try {
    const parsed: unknown = JSON.parse(sessionStorage.getItem(PENDING_KEY) ?? '[]');
    return Array.isArray(parsed) ? parsed.filter(isOfferedProps) : [];
  } catch {
    return [];
  }
}

function writeStash(entries: RecoveryOfferedProps[]): void {
  try {
    if (entries.length === 0) sessionStorage.removeItem(PENDING_KEY);
    else sessionStorage.setItem(PENDING_KEY, JSON.stringify(entries));
  } catch {
    // Storage gone: so is the stash.
  }
}

/** Send now when analytics can, otherwise keep it for the next flush. */
export function reportRecoveryOffered(props: RecoveryOfferedProps): void {
  if (safeAnalytics.isReady()) {
    safeAnalytics.track(ANALYTICS_EVENTS.VOICE_RECORDING_RECOVERY_OFFERED, props);
    return;
  }
  writeStash([...readStash(), props].slice(-20));
}

/** Deliver stashed offers. Checks readiness BEFORE clearing, as the model does. */
export function flushPendingRecordingRecoveryOffered(): void {
  const pending = readStash();
  if (pending.length === 0 || !safeAnalytics.isReady()) return;
  for (const props of pending) {
    safeAnalytics.track(ANALYTICS_EVENTS.VOICE_RECORDING_RECOVERY_OFFERED, props);
  }
  writeStash([]);
}
